import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  Loader,
  MapPinIcon,
  Building,
  Search,
  ArrowRight,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoffee,
  faSquareParking,
  faMosque,
  faTrain,
  faDoorOpen,
  faElevator,
  faRestroom,
  faCircleInfo,
  faCouch,
  faLocationDot,
  faHospital,
  faUserDoctor,
  faSyringe,
  faBriefcaseMedical,
  faXRay,
  faPills,
  faStethoscope,
  faBed,
  faTruckMedical,
  faLungs,
  faBaby,
  faMicroscope,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import MapView from "../components/MapView";
import LandmarksPanel from "../components/LandmarksPanel";
import PositioningPanel from "../components/PositioningPanel";
import NavigationBar from "../components/NavigationBar";
import Navigation3DPanel from "../components/Navigation3DPanel";
import Map3DZones from "../components/Map3DZones";
import { Department, Landmark, Route } from "../types";

type Screen = "home" | "browse" | "find" | "directions" | "map3d";

const HomeScreen: React.FC = () => {
  const [currentScreen, setCurrentScreen] = useState<Screen>("home");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [landmarks, setLandmarks] = useState<Landmark[]>([]);
  const [selectedDestination, setSelectedDestination] = useState<
    Department | Landmark | null
  >(null);
  const [route, setRoute] = useState<Route | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [destinationSearchQuery, setDestinationSearchQuery] = useState("");
  const [showDestinationDropdown, setShowDestinationDropdown] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(true);

  // Resize Mapbox map after left panel collapse/expand transition completes
  useEffect(() => {
    const timer = setTimeout(() => {
      // Find the mapboxgl canvas and trigger resize on its map instance
      const mapCanvas = document.querySelector(
        ".mapboxgl-canvas",
      ) as HTMLCanvasElement | null;
      if (mapCanvas) {
        // Dispatch a resize event so Mapbox recalculates its container size
        window.dispatchEvent(new Event("resize"));
      }
    }, 320); // slightly after the 300ms CSS transition
    return () => clearTimeout(timer);
  }, [leftPanelCollapsed]);

  // Kiosk detection
  const [kioskId, setKioskId] = useState("kiosk_1");
  const [kioskData, setKioskData] = useState<any>(null);

  const [messageAlert, setMessageAlert] = useState<{
    type: "info" | "success" | "error";
    text: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [backgroundImageError, setBackgroundImageError] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch departments
        const deptResponse = await fetch("/api/departments");
        const deptData = await deptResponse.json();
        if (deptData.success) setDepartments(deptData.data);

        // Fetch landmarks
        const landmarkResponse = await fetch("/api/landmarks");
        const landmarkData = await landmarkResponse.json();
        if (landmarkData.success) setLandmarks(landmarkData.data);

        // Fetch kiosks to get current location
        const kioskResponse = await fetch("/api/kiosks");
        const kioskDataList = await kioskResponse.json();
        if (kioskDataList.success && kioskDataList.data.length > 0) {
          // Find default kiosk or use first one
          const defaultKiosk = kioskDataList.data.find(
            (k: any) => k.is_default === true,
          );
          const selectedKiosk = defaultKiosk || kioskDataList.data[0];
          setKioskId(selectedKiosk.id);
          setKioskData(selectedKiosk);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setMessageAlert({ type: "error", text: "Failed to load data" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch route when destination changes
  useEffect(() => {
    if (!selectedDestination || !kioskId) {
      console.log(
        "Skipping route fetch - destination:",
        selectedDestination,
        "kioskId:",
        kioskId,
      );
      return;
    }

    const fetchRoute = async () => {
      try {
        setLoading(true);
        console.log(
          "Fetching route from",
          kioskId,
          "to",
          selectedDestination.id,
        );

        // Fetch route
        const response = await fetch(
          `/api/routes?from=${kioskId}&to=${selectedDestination.id}`,
        );
        const data = await response.json();

        console.log("Route API response:", data);

        if (data.success && data.data && data.data.route) {
          console.log("Route received successfully:", data.data.route);
          setRoute(data.data.route);
          setCurrentStepIndex(0);
        } else {
          console.error("Route fetch failed:", data);
          setMessageAlert({
            type: "error",
            text: data.error || "Failed to calculate route",
          });
          // Set a dummy route so the map still shows
          setRoute({
            from: { x: 0, y: 0 },
            to: { x: 0, y: 0 },
            totalDistance: 0,
            segments: [],
            landmarks: [],
          });
        }
      } catch (error) {
        console.error("Failed to fetch route:", error);
        setMessageAlert({ type: "error", text: "Failed to calculate route" });
        // Set a dummy route so the map still shows
        setRoute({
          from: { x: 0, y: 0 },
          to: { x: 0, y: 0 },
          totalDistance: 0,
          segments: [],
          landmarks: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [selectedDestination, kioskId]);

  const handleGoHome = () => {
    setCurrentScreen("home");
    setSelectedDestination(null);
    setRoute(null);
  };

  const handleNavigate = (screen: Screen) => {
    setCurrentScreen(screen);
    setSelectedDestination(null);
    setRoute(null);
  };

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  return (
    <div className="app-container">
      {/* Navigation Bar */}
      <NavigationBar
        currentScreen={currentScreen}
        kioskData={kioskData}
        onNavigate={handleNavigate}
      />

      {/* Alert Messages */}
      <AnimatePresence>
        {messageAlert && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mx-4 mt-4 p-4 rounded-lg flex items-center gap-3 shadow-lg"
            style={{
              backgroundColor:
                messageAlert.type === "error"
                  ? "#fee2e2"
                  : messageAlert.type === "success"
                    ? "#dcfce7"
                    : "#dbeafe",
              color:
                messageAlert.type === "error"
                  ? "#7f1d1d"
                  : messageAlert.type === "success"
                    ? "#166534"
                    : "#0c2d48",
              borderLeft: `4px solid ${messageAlert.type === "error" ? "#dc2626" : messageAlert.type === "success" ? "#22c55e" : "#0ea5e9"}`,
            }}
          >
            <AlertCircle size={20} />
            <span className="text-sm font-medium">{messageAlert.text}</span>
            <button
              onClick={() => setMessageAlert(null)}
              className="ml-auto text-lg leading-none hover:opacity-70 transition-opacity"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        <AnimatePresence mode="wait">
          {/* Home Screen */}
          {currentScreen === "home" && (
            <motion.div
              key="home"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="h-full w-full px-4 md:px-8 py-8 overflow-auto home-screen-container"
            >
              {/* Optional Background Image */}
              {!backgroundImageError && (
                <div
                  className="home-screen-background with-overlay"
                  style={{
                    backgroundImage: "url('/shared/images/LandingPage1.jpg')",
                  }}
                  onError={() => setBackgroundImageError(true)}
                />
              )}

              {loading ? (
                <div className="flex items-center justify-center h-full home-screen-content">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="text-blue-500"
                  >
                    <Loader size={48} />
                  </motion.div>
                </div>
              ) : (
                <div className="h-full home-screen-content overflow-y-auto pb-40">
                  {/* Clean Header */}
                  <div
                    className="border-b py-2 px-4 md:px-8"
                    style={{
                      backgroundColor: "var(--surface-2)",
                      borderColor: "var(--surface-3)",
                    }}
                  >
                    <div className="max-w-4xl mx-auto text-center">
                      <div className="flex items-center justify-center gap-4 mb-1">
                        <img
                          src="/shared/images/logo-01.png"
                          alt="Hospital Logo"
                          className="h-16 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          loading="lazy"
                        />
                      </div>

                      <div className="flex justify-center mb-1">
                        <img
                          src="/Icons/Logo.png"
                          alt="Habib Medical Group"
                          className="h-32 object-contain"
                        />
                      </div>
                      <motion.p
                        initial={{ x: -100, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{
                          duration: 0.8,
                          ease: "easeOut",
                        }}
                        className="text-lg mb-6"
                        style={{ color: "var(--color-text-secondary)" }}
                      >
                        <motion.span
                          animate={{
                            opacity: [1, 0.5, 1, 0.5, 1, 0.5, 1],
                          }}
                          transition={{
                            duration: 6,
                            delay: 0.9,
                            times: [0, 0.14, 0.28, 0.42, 0.56, 0.7, 1],
                          }}
                        >
                          Let me help you to find your way!
                        </motion.span>
                      </motion.p>

                      {/* Search Bar - Centered and Smaller */}
                      <div className="relative max-w-xl mx-auto">
                        <Search
                          className="absolute left-4 top-1/2 transform -translate-y-1/2"
                          style={{ color: "var(--color-text-muted)" }}
                          size={20}
                        />
                        <input
                          type="text"
                          placeholder="Search your destination..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
                          style={{
                            backgroundColor: "var(--surface-3)",
                            border: "1px solid var(--surface-4)",
                            color: "var(--color-text-primary)",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Split Screen Content Area */}
                  <div className="flex h-full">
                    {/* Departments Section - Left Side (60%) */}
                    <div className="w-full overflow-y-auto p-1 md:p-1">
                      <div className="flex items-center gap-2 mb-4"></div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {departments
                          .filter(
                            (dept) =>
                              dept.name
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase()) ||
                              dept.description
                                ?.toLowerCase()
                                .includes(searchQuery.toLowerCase()) ||
                              dept.serviceType
                                ?.toLowerCase()
                                .includes(searchQuery.toLowerCase()),
                          )
                          .map((dept, index) => {
                            // Get Font Awesome icon with color based on department category/name
                            const getDeptIcon = () => {
                              const name = dept.name.toLowerCase();
                              const category =
                                dept.category?.toLowerCase() || "";
                              const serviceType =
                                dept.serviceType?.toLowerCase() || "";

                              if (
                                name.includes("respiratory") ||
                                name.includes("pulmonary") ||
                                name.includes("lung")
                              ) {
                                return { icon: faLungs, color: "#06B6D4" }; // Cyan
                              } else if (
                                name.includes("pediatric") ||
                                name.includes("paediatric") ||
                                name.includes("child") ||
                                name.includes("neonatal")
                              ) {
                                return { icon: faBaby, color: "#EC4899" }; // Pink
                              } else if (
                                name.includes("diagnostic") ||
                                name.includes("pathology")
                              ) {
                                return { icon: faMicroscope, color: "#8B5CF6" }; // Purple
                              } else if (
                                name.includes("icu") ||
                                name.includes("intensive") ||
                                name.includes("critical") ||
                                category.includes("icu")
                              ) {
                                return { icon: faBed, color: "#F97316" }; // Orange
                              } else if (
                                name.includes("emergency") ||
                                category.includes("emergency")
                              ) {
                                return {
                                  icon: faTruckMedical,
                                  color: "#EF4444",
                                }; // Red
                              } else if (
                                name.includes("surgery") ||
                                name.includes("operating") ||
                                category.includes("surgery")
                              ) {
                                return {
                                  icon: faBriefcaseMedical,
                                  color: "#3B82F6",
                                }; // Blue
                              } else if (
                                name.includes("cardio") ||
                                name.includes("heart") ||
                                name.includes("cardiac")
                              ) {
                                return { icon: faHeart, color: "#EF4444" }; // Red
                              } else if (
                                name.includes("lab") ||
                                category.includes("lab")
                              ) {
                                return { icon: faSyringe, color: "#14B8A6" }; // Teal
                              } else if (
                                name.includes("radio") ||
                                name.includes("imaging") ||
                                name.includes("x-ray") ||
                                category.includes("radiology")
                              ) {
                                return { icon: faXRay, color: "#A855F7" }; // Purple
                              } else if (
                                name.includes("pharmacy") ||
                                name.includes("drug")
                              ) {
                                return { icon: faPills, color: "#EC4899" }; // Pink
                              } else if (
                                name.includes("opd") ||
                                name.includes("outpatient") ||
                                name.includes("clinic") ||
                                category.includes("opd")
                              ) {
                                return { icon: faUserDoctor, color: "#22C55E" }; // Green
                              } else if (
                                serviceType.includes("consultation") ||
                                serviceType.includes("checkup")
                              ) {
                                return {
                                  icon: faStethoscope,
                                  color: "#0EA5E9",
                                }; // Sky blue
                              } else {
                                return { icon: faHospital, color: "#6B7280" }; // Gray
                              }
                            };

                            const iconData = getDeptIcon();

                            return (
                              <motion.div
                                key={dept.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.02 }}
                                className="border rounded-xl p-3 hover:shadow-lg transition-all group cursor-pointer relative min-h-[92px]"
                                style={{
                                  backgroundColor: "var(--surface-2)",
                                  borderColor: "var(--surface-3)",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "var(--surface-3)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "var(--surface-2)")
                                }
                                onClick={() => {
                                  setSelectedDestination(dept);
                                  setCurrentScreen("find");
                                }}
                              >
                                {/* Department Name - Top Left */}
                                <h3
                                  className="font-semibold text-xs leading-tight mb-1"
                                  style={{ color: "var(--color-text-primary)" }}
                                >
                                  {dept.name}
                                </h3>

                                {/* Arabic text - Right Middle */}
                                {dept.description && (
                                  <p
                                    className="text-xs leading-tight text-right pr-1"
                                    style={{
                                      color: "var(--color-text-secondary)",
                                    }}
                                    dir="rtl"
                                  >
                                    {dept.description}
                                  </p>
                                )}

                                {/* Font Awesome Icon - Bottom Left */}
                                <div className="absolute bottom-3 left-3">
                                  <FontAwesomeIcon
                                    icon={iconData.icon}
                                    size="xl"
                                    style={{ color: iconData.color }}
                                  />
                                </div>

                                {/* Navigate button - Bottom Right - appears on hover */}
                                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
                                    <ArrowRight
                                      size={14}
                                      className="text-white"
                                    />
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                  {/* Safe-zone gradient fade */}
                  <div
                    className="pointer-events-none fixed bottom-0 left-0 w-full h-44 z-40"
                    style={{
                      background:
                        "linear-gradient(to top, var(--surface-1) 0%, transparent 100%)",
                    }}
                  />
                  {/* Floating Landmarks Dock */}
                  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
                    <div
                      className="backdrop-blur-md shadow-xl rounded-2xl px-8 py-2"
                      style={{
                        backgroundColor: "rgba(30,41,59,0.97)",
                        border: "1px solid var(--surface-3)",
                      }}
                    >
                      {/* ── NEW: Grouped landmark categories — nearest auto-selected ── */}
                      {(() => {
                        // Haversine distance between two lat/lon points (returns metres)
                        const haversine = (
                          lat1: number,
                          lon1: number,
                          lat2: number,
                          lon2: number,
                        ) => {
                          const R = 6371000;
                          const dLat = ((lat2 - lat1) * Math.PI) / 180;
                          const dLon = ((lon2 - lon1) * Math.PI) / 180;
                          const a =
                            Math.sin(dLat / 2) ** 2 +
                            Math.cos((lat1 * Math.PI) / 180) *
                              Math.cos((lat2 * Math.PI) / 180) *
                              Math.sin(dLon / 2) ** 2;
                          return (
                            R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
                          );
                        };

                        // Kiosk position for nearest-calculation
                        const kioskLat =
                          kioskData?.coordinates?.latitude ?? 24.812;
                        const kioskLon =
                          kioskData?.coordinates?.longitude ?? 46.624;
                        const kioskFloor: string =
                          (kioskData as any)?.floor || "G";

                        // Category definitions — label, icon, keywords to match landmark names
                        const categories = [
                          {
                            key: "elevator",
                            label: "Elevator",
                            labelAr: "مصعد",
                            icon: faElevator,
                            color: "#6366F1",
                            keywords: ["elevator", "lift"],
                          },
                          {
                            key: "toilet",
                            label: "Toilets",
                            labelAr: "دورة مياه",
                            icon: faRestroom,
                            color: "#06B6D4",
                            keywords: [
                              "toilet",
                              "toilets",
                              "restroom",
                              "washroom",
                              "wc",
                            ],
                          },
                          {
                            key: "escalator",
                            label: "Escalator",
                            labelAr: "سلم متحرك",
                            icon: faElevator,
                            color: "#8B5CF6",
                            keywords: ["escalator"],
                          },
                          {
                            key: "cafe",
                            label: "Café",
                            labelAr: "مقهى",
                            icon: faCoffee,
                            color: "#8B4513",
                            keywords: ["cafe", "cafeteria", "coffee", "costa"],
                          },
                          {
                            key: "parking",
                            label: "Parking",
                            labelAr: "موقف سيارات",
                            icon: faSquareParking,
                            color: "#3B82F6",
                            keywords: ["parking"],
                          },
                          {
                            key: "mosque",
                            label: "Prayer",
                            labelAr: "مصلى",
                            icon: faMosque,
                            color: "#10B981",
                            keywords: ["mosque", "prayer", "masjid"],
                          },
                          {
                            key: "info",
                            label: "Info Desk",
                            labelAr: "مكتب المعلومات",
                            icon: faCircleInfo,
                            color: "#0EA5E9",
                            keywords: ["info", "information", "appointment"],
                          },
                          {
                            key: "waiting",
                            label: "Waiting",
                            labelAr: "منطقة الانتظار",
                            icon: faCouch,
                            color: "#EC4899",
                            keywords: ["waiting"],
                          },
                          {
                            key: "other",
                            label: "Other",
                            labelAr: "أخرى",
                            icon: faLocationDot,
                            color: "#EF4444",
                            keywords: [],
                          },
                        ];

                        // Group landmarks into categories
                        const grouped: Record<string, Landmark[]> = {};
                        const assigned = new Set<string>();

                        categories.forEach((cat) => {
                          if (cat.key === "other") return;
                          grouped[cat.key] = landmarks.filter((l) => {
                            const n = l.name.toLowerCase();
                            const match = cat.keywords.some((kw) =>
                              n.includes(kw),
                            );
                            if (match) assigned.add(l.id);
                            return match;
                          });
                        });
                        grouped["other"] = landmarks.filter(
                          (l) => !assigned.has(l.id),
                        );

                        // Filter by search
                        const visibleCats = categories.filter((cat) => {
                          if (!searchQuery) return grouped[cat.key]?.length > 0;
                          return grouped[cat.key]?.some((l) =>
                            l.name
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase()),
                          );
                        });

                        const findNearest = (items: Landmark[]) => {
                          if (items.length === 0) return null;

                          // Prefer landmarks on the same floor as the kiosk
                          const sameFloor = items.filter(
                            (l) => ((l as any).floor || "G") === kioskFloor,
                          );
                          const pool = sameFloor.length > 0 ? sameFloor : items;

                          return pool.reduce((best, l) => {
                            const lat = l.coordinates?.latitude ?? 0;
                            const lon = l.coordinates?.longitude ?? 0;
                            const bLat = best.coordinates?.latitude ?? 0;
                            const bLon = best.coordinates?.longitude ?? 0;
                            return haversine(kioskLat, kioskLon, lat, lon) <
                              haversine(kioskLat, kioskLon, bLat, bLon)
                              ? l
                              : best;
                          });
                        };

                        return (
                          <div className="flex gap-8 px-3">
                            {visibleCats.map((cat, index) => {
                              const items = grouped[cat.key] ?? [];
                              const nearest = findNearest(items);
                              if (!nearest) return null;
                              return (
                                <motion.div
                                  key={cat.key}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.03 }}
                                  className="flex flex-col items-center gap-1 cursor-pointer group"
                                  onClick={() => {
                                    setSelectedDestination(nearest);
                                    setCurrentScreen("find");
                                  }}
                                >
                                  <motion.div
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.92 }}
                                    className="w-14 h-14 rounded-full flex items-center justify-center shadow-md transition-all group-hover:shadow-lg"
                                    style={{
                                      backgroundColor: cat.color + "1a",
                                      border: `2px solid ${cat.color}44`,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={cat.icon}
                                      size="xl"
                                      style={{ color: cat.color }}
                                    />
                                  </motion.div>
                                  <p
                                    className="text-center text-[10px] font-medium leading-tight line-clamp-2 w-full px-1"
                                    style={{
                                      color: "var(--color-text-secondary)",
                                    }}
                                  >
                                    {cat.label}
                                  </p>
                                  <p
                                    className="text-center text-[9px] text-gray-400 leading-tight w-full px-1"
                                    dir="rtl"
                                  >
                                    {cat.labelAr}
                                  </p>
                                </motion.div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* ── END NEW grouped layout ── */}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Find Way Screen */}
          {currentScreen === "find" && (
            <motion.div
              key="find"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="h-full w-full flex"
            >
              {/* Left Side Container with Collapse Button */}
              <div className="relative flex h-full">
                {/* Collapsible Left Panel */}
                <div
                  className="h-full flex flex-col border-r transition-all duration-300 overflow-hidden"
                  style={{
                    width: leftPanelCollapsed ? "0px" : "384px",
                    backgroundColor: "var(--surface-2)",
                    borderColor: "var(--surface-3)",
                  }}
                >
                  {/* Navigation Steps Section - Above Find Destination */}
                  {route &&
                    route.segments &&
                    route.segments.length > 0 &&
                    selectedDestination && (
                      <div
                        className="border-b"
                        style={{ borderColor: "var(--surface-3)" }}
                      >
                        {/* Destination Header */}
                        <div
                          className="p-4 border-b"
                          style={{
                            backgroundColor: "var(--surface-3)",
                            borderColor: "var(--surface-4)",
                          }}
                        >
                          <div className="flex items-start gap-3">
                            {/* Icon Column with Dotted Line */}
                            <div className="flex flex-col items-center pt-3">
                              <div
                                className="w-4 h-4 rounded-full border-2 border-gray-500"
                                style={{ backgroundColor: "var(--surface-2)" }}
                              ></div>
                              <div className="w-0.5 h-8 border-l-2 border-dotted border-gray-500 my-1"></div>
                              <MapPinIcon size={20} className="text-red-400" />
                            </div>

                            {/* Input Fields Column */}
                            <div className="flex-1 space-y-2">
                              <input
                                type="text"
                                value={kioskData?.name || "Your Location"}
                                readOnly
                                className="w-full rounded-lg px-3 py-2 text-sm"
                                style={{
                                  backgroundColor: "var(--surface-2)",
                                  border: "1px solid var(--surface-4)",
                                  color: "var(--color-text-primary)",
                                }}
                              />
                              <div className="relative">
                                <input
                                  type="text"
                                  value={
                                    destinationSearchQuery ||
                                    selectedDestination.name
                                  }
                                  onChange={(e) => {
                                    setDestinationSearchQuery(e.target.value);
                                    setShowDestinationDropdown(true);
                                  }}
                                  onFocus={() =>
                                    setShowDestinationDropdown(true)
                                  }
                                  placeholder="Search destination..."
                                  className="w-full rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  style={{
                                    backgroundColor: "var(--surface-2)",
                                    border: "1px solid var(--surface-4)",
                                    color: "var(--color-text-primary)",
                                  }}
                                />
                                {/* Dropdown for search results */}
                                {showDestinationDropdown &&
                                  destinationSearchQuery && (
                                    <div
                                      className="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50"
                                      style={{
                                        backgroundColor: "var(--surface-2)",
                                        border: "1px solid var(--surface-4)",
                                      }}
                                    >
                                      {/* Landmarks */}
                                      {landmarks
                                        .filter((l) =>
                                          l.name
                                            .toLowerCase()
                                            .includes(
                                              destinationSearchQuery.toLowerCase(),
                                            ),
                                        )
                                        .map((landmark) => (
                                          <button
                                            key={landmark.id}
                                            onClick={() => {
                                              setSelectedDestination(landmark);
                                              setDestinationSearchQuery("");
                                              setShowDestinationDropdown(false);
                                            }}
                                            className="w-full text-left px-3 py-2 transition-colors border-b"
                                            style={{
                                              borderColor: "var(--surface-3)",
                                            }}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.backgroundColor =
                                                "var(--surface-3)")
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.backgroundColor =
                                                "")
                                            }
                                          >
                                            <p
                                              className="text-sm font-semibold"
                                              style={{
                                                color:
                                                  "var(--color-text-primary)",
                                              }}
                                            >
                                              {landmark.name}
                                            </p>
                                            <p
                                              className="text-xs"
                                              style={{
                                                color:
                                                  "var(--color-text-muted)",
                                              }}
                                            >
                                              Landmark
                                            </p>
                                          </button>
                                        ))}
                                      {/* Departments */}
                                      {departments
                                        .filter((d) =>
                                          d.name
                                            .toLowerCase()
                                            .includes(
                                              destinationSearchQuery.toLowerCase(),
                                            ),
                                        )
                                        .map((dept) => (
                                          <button
                                            key={dept.id}
                                            onClick={() => {
                                              setSelectedDestination(dept);
                                              setDestinationSearchQuery("");
                                              setShowDestinationDropdown(false);
                                            }}
                                            className="w-full text-left px-3 py-2 transition-colors border-b"
                                            style={{
                                              borderColor: "var(--surface-3)",
                                            }}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.backgroundColor =
                                                "var(--surface-3)")
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.backgroundColor =
                                                "")
                                            }
                                          >
                                            <p
                                              className="text-sm font-semibold"
                                              style={{
                                                color:
                                                  "var(--color-text-primary)",
                                              }}
                                            >
                                              {dept.name}
                                            </p>
                                            <p
                                              className="text-xs"
                                              style={{
                                                color:
                                                  "var(--color-text-muted)",
                                              }}
                                            >
                                              Department
                                            </p>
                                          </button>
                                        ))}
                                      {/* No results */}
                                      {landmarks.filter((l) =>
                                        l.name
                                          .toLowerCase()
                                          .includes(
                                            destinationSearchQuery.toLowerCase(),
                                          ),
                                      ).length === 0 &&
                                        departments.filter((d) =>
                                          d.name
                                            .toLowerCase()
                                            .includes(
                                              destinationSearchQuery.toLowerCase(),
                                            ),
                                        ).length === 0 && (
                                          <div
                                            className="px-3 py-4 text-center text-sm"
                                            style={{
                                              color: "var(--color-text-muted)",
                                            }}
                                          >
                                            No destinations found
                                          </div>
                                        )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Route Options */}
                        <div className="max-h-80 overflow-y-auto">
                          {route.segments.map((segment: any, index: number) => {
                            const distanceInMeters =
                              typeof segment.distance === "number"
                                ? segment.distance
                                : segment.distance
                                  ? parseFloat(
                                      String(segment.distance).replace(
                                        /[^0-9.]/g,
                                        "",
                                      ),
                                    )
                                  : 0;
                            const timeInMinutes = Math.max(
                              1,
                              Math.round(distanceInMeters / 84),
                            );

                            return (
                              <div
                                key={index}
                                className="p-4 border-b cursor-pointer transition-colors"
                                style={{ borderColor: "var(--surface-3)" }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "var(--surface-3)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor = "")
                                }
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 pt-1">
                                    <svg
                                      className="w-6 h-6"
                                      style={{
                                        color: "var(--color-text-secondary)",
                                      }}
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7" />
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    {segment.instruction
                                      .split(/\.\s+|\n/)
                                      .filter(Boolean)
                                      .map((line: string, i: number) => (
                                        <p
                                          key={i}
                                          className="text-sm font-medium leading-snug mb-1"
                                          style={{
                                            color: "var(--color-text-primary)",
                                          }}
                                        >
                                          {line.endsWith(".")
                                            ? line
                                            : `${line}.`}
                                        </p>
                                      ))}
                                  </div>
                                  <div className="flex-shrink-0 text-right pt-1">
                                    <p
                                      className="text-sm font-semibold"
                                      style={{
                                        color: "var(--color-text-primary)",
                                      }}
                                    >
                                      {timeInMinutes} min
                                    </p>
                                    {distanceInMeters > 0 && (
                                      <p
                                        className="text-xs mt-1"
                                        style={{
                                          color: "var(--color-text-muted)",
                                        }}
                                      >
                                        {distanceInMeters}m
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Find Destination Panel - Below Navigation Steps */}
                  <div className="flex-1 overflow-hidden">
                    <LandmarksPanel
                      landmarks={landmarks}
                      departments={departments}
                      onSelectLandmark={(landmark) => {
                        setSelectedDestination(landmark);
                      }}
                      onSelectDepartment={(dept) => {
                        setSelectedDestination(dept);
                      }}
                      onBack={handleGoHome}
                      kioskData={kioskData}
                    />
                  </div>
                </div>

                {/* Collapse/Expand Button */}
                <button
                  onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
                  className="absolute top-1/2 transform -translate-y-1/2 rounded-r-lg shadow-md transition-all z-20"
                  style={{
                    left: leftPanelCollapsed ? "0px" : "384px",
                    width: "32px",
                    height: "80px",
                    backgroundColor: "var(--surface-3)",
                    border: "1px solid var(--surface-4)",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {leftPanelCollapsed ? (
                    <svg
                      className="w-5 h-5 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Map on Right - Flex to fill remaining space */}
              <div
                className="flex-1 min-h-0"
                id="map-container-wrapper"
                style={{ minWidth: 0, height: "100%" }}
              >
                <MapView
                  kioskId={kioskId}
                  kioskData={kioskData}
                  destination={selectedDestination}
                  onBack={handleGoHome}
                  allDepartments={departments}
                  landmarks={landmarks}
                  route={route}
                  showAllLocations={true}
                  highlightKiosk={true}
                  onFloorChange={() => {
                    setRoute(null);
                  }}
                />
              </div>
            </motion.div>
          )}

          {/* Directions Screen */}
          {currentScreen === "directions" && (
            <motion.div
              key="directions"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="h-full w-full flex"
            >
              {!selectedDestination ? (
                // Show destination selection if no destination is selected
                <div className="w-full h-full flex">
                  <div
                    className="w-full md:w-96 h-full flex flex-col border-r"
                    style={{
                      backgroundColor: "var(--surface-2)",
                      borderColor: "var(--surface-3)",
                    }}
                  >
                    <LandmarksPanel
                      landmarks={landmarks}
                      departments={departments}
                      onSelectLandmark={(landmark) => {
                        setSelectedDestination(landmark);
                      }}
                      onSelectDepartment={(dept) => {
                        setSelectedDestination(dept);
                      }}
                      onBack={handleGoHome}
                      kioskData={kioskData}
                    />
                  </div>
                  <div className="flex-1 min-h-0" style={{ minWidth: 0 }}>
                    <MapView
                      kioskId={kioskId}
                      kioskData={kioskData}
                      destination={null}
                      onBack={handleGoHome}
                      allDepartments={departments}
                      landmarks={landmarks}
                      route={null}
                      showAllLocations={true}
                      highlightKiosk={true}
                    />
                  </div>
                </div>
              ) : loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="text-blue-500 mx-auto mb-4"
                    >
                      <Loader size={48} />
                    </motion.div>
                    <p
                      className="text-lg"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      Calculating route to {selectedDestination.name}...
                    </p>
                  </div>
                </div>
              ) : route && route.segments && route.segments.length > 0 ? (
                <>
                  <PositioningPanel
                    kioskId={kioskId}
                    destination={selectedDestination as any}
                    route={route}
                    onBack={handleGoHome}
                    onStepChange={setCurrentStepIndex}
                  />

                  <div className="flex-1 min-h-0" style={{ minWidth: 0 }}>
                    <MapView
                      kioskId={kioskId}
                      kioskData={kioskData}
                      destination={selectedDestination}
                      onBack={handleGoHome}
                      allDepartments={departments}
                      landmarks={landmarks}
                      route={route}
                      currentStepIndex={currentStepIndex}
                      onFloorChange={() => {
                        setRoute(null);
                      }}
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col">
                  <div
                    className="p-4 border-b"
                    style={{
                      backgroundColor: "#422006",
                      borderColor: "#92400e",
                    }}
                  >
                    <p className="text-sm" style={{ color: "#FCD34D" }}>
                      ⚠️ Route calculation unavailable. Showing destination on
                      map.
                    </p>
                  </div>
                  <div className="flex-1">
                    <MapView
                      kioskId={kioskId}
                      kioskData={kioskData}
                      destination={selectedDestination}
                      onBack={handleGoHome}
                      allDepartments={departments}
                      landmarks={landmarks}
                      route={null}
                      showAllLocations={true}
                      highlightKiosk={true}
                      onFloorChange={() => {
                        setRoute(null);
                      }}
                    />
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* 3D Virtual */}
          {currentScreen === "map3d" && (
            <motion.div
              key="map3d"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="h-full w-full flex"
            >
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="text-blue-500 mx-auto mb-4"
                    >
                      <Loader size={48} />
                    </motion.div>
                    <p className="text-gray-600 text-lg">
                      Loading 3D Virtual Map...
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Navigation Panel */}
                  <Navigation3DPanel
                    selectedDestination={selectedDestination}
                    onBack={handleGoHome}
                    onStartingPointChange={() => {}}
                    selectedStartingPoint={null}
                    kioskData={kioskData}
                    route={route}
                    departments={departments}
                    landmarks={landmarks}
                    onSelectDepartment={(dept) => {
                      setSelectedDestination(dept);
                    }}
                    onSelectLandmark={(landmark) => {
                      setSelectedDestination(landmark);
                    }}
                  />

                  {/* 3D Map */}
                  <div className="flex-1">
                    <Map3DZones
                      departments={departments}
                      landmarks={landmarks}
                      selectedDestination={selectedDestination}
                      kioskData={kioskData}
                    />
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HomeScreen;
